import { useGetConfig } from '@features/creator/hooks/useGetConfig'
import { usePostConfig } from '@features/creator/hooks/usePostConfig'
import { createContext, ReactNode, useEffect, useState } from 'react'

type ConfigProviderProps = {
  children: ReactNode
}

export type ConfigProviderState = {
  config: {
    primary: string
    secondary: string
    error: string
    warning: string
    info: string
    success: string
    backgroundPaper: string
    backgroundDefault: string
    backgroundPaperDark?: string
    backgroundDefaultDark?: string
    files?: undefined
    logo: string
    companyName: string
    email: string
    phone: string
  }
  onChange?: (newConfig: ConfigProviderState['config'] & { onGenerateCode: (code: string) => void }) => void
}

const initialState: ConfigProviderState['config'] = {
  primary: '#1976d2',
  secondary: '#072B73',
  error: '#ff1744',
  warning: '#ff9800',
  info: '#00B5E2',
  success: '#4caf50',
  backgroundPaper: '#fff',
  backgroundDefault: '#f5f5f5',
  backgroundPaperDark: '#15171e',
  backgroundDefaultDark: '#2c2d30',
  logo: '',
  companyName: 'NeuroSYS',
  email: 'info@neurosys.com',
  phone: '+48 537 900 002',
}

export const ConfigProviderContext = createContext<ConfigProviderState>({ config: initialState })

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const searchCode: string | null = new URLSearchParams(window.location.search).get('code')
  const ITEM_NAME = 'config'
  const CODE_NAME = 'code'
  const savedConfig = localStorage.getItem(ITEM_NAME)
  const { mutate } = usePostConfig()
  const { data, isLoading } = useGetConfig(searchCode || localStorage.getItem(CODE_NAME))
  const [config, setConfig] = useState<ConfigProviderState['config']>(() =>
    savedConfig ? (JSON.parse(savedConfig) as ConfigProviderState['config']) : initialState
  )

  const onChange = ({
    onGenerateCode,
    ...newConfig
  }: ConfigProviderState['config'] & { onGenerateCode: (code: string) => void }) => {
    setConfig(newConfig)
    const code = generateRandomCode()
    onGenerateCode?.(code)
    localStorage.setItem(code, JSON.stringify(newConfig))
    mutate({ code, ...newConfig })
  }

  useEffect(() => {
    if (data) {
      setConfig(data)
    }
  }, [data])

  if (isLoading) return null

  return <ConfigProviderContext.Provider value={{ config, onChange }}>{children}</ConfigProviderContext.Provider>
}

function generateRandomCode(length = 8) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return result
}
